import api from '../api';

export interface BoardSettings {
  contactName?: string;
  contactPhone?: string;
  noticeLines?: string[];
}

export interface ReservationBoard {
  id: number;
  name: string;
  description: string | null;
  userId: number;
  resourceNames: string[];
  settings: BoardSettings | null;
  isActive: boolean;
  countryCode: string;
  shareUrl?: string | null;
  createdAt: string;
  updatedAt: string;
  lastUsedAt?: string;
}

export interface RsHolidayEntry {
  day: number;
  name: string;
}

export interface ShareTokenResult {
  shareToken: string;
  shareUrl: string;
}

export interface ReservationBooking {
  id: number;
  boardId: number;
  userId: number | null;
  resourceName: string;
  startTime: string;
  endTime: string;
  displayName: string | null;
  department: string | null;
  notes: string | null;
  hasDeletePassword: boolean;
  createdAt: string;
  updatedAt: string;
  user?: {
    id: number;
    username: string;
    firstName: string | null;
    lastName: string | null;
  };
}

export interface CreateBoardInput {
  name: string;
  description?: string;
  resourceNames: string[];
  settings?: BoardSettings;
  countryCode?: string;
}

export interface UpdateBoardInput {
  name?: string;
  description?: string | null;
  resourceNames?: string[];
  settings?: BoardSettings | null;
  isActive?: boolean;
  countryCode?: string;
}

export interface CreateBookingInput {
  resourceNames: string[];
  startTime: string;
  endTime: string;
  displayName: string;
  department?: string;
  notes?: string;
  deletePassword?: string;
}

export const reservationBoardService = {
  async listBoards(): Promise<ReservationBoard[]> {
    const response = await api.get<{ success: boolean; data: ReservationBoard[] }>(
      '/reservation-system/boards'
    );
    return response.data.data;
  },

  async getBoard(id: number): Promise<ReservationBoard> {
    const response = await api.get<{ success: boolean; data: ReservationBoard }>(
      `/reservation-system/boards/${id}`
    );
    return response.data.data;
  },

  async createBoard(input: CreateBoardInput): Promise<ReservationBoard> {
    const response = await api.post<{ success: boolean; data: ReservationBoard }>(
      '/reservation-system/boards',
      input
    );
    return response.data.data;
  },

  async updateBoard(id: number, input: UpdateBoardInput): Promise<ReservationBoard> {
    const response = await api.patch<{ success: boolean; data: ReservationBoard }>(
      `/reservation-system/boards/${id}`,
      input
    );
    return response.data.data;
  },

  async deleteBoard(id: number): Promise<void> {
    await api.delete(`/reservation-system/boards/${id}`);
  },

  async issueShareToken(id: number): Promise<ShareTokenResult> {
    const response = await api.post<{ success: boolean; data: ShareTokenResult }>(
      `/reservation-system/boards/${id}/share-token`
    );
    return response.data.data;
  },

  async revokeShareToken(id: number): Promise<void> {
    await api.delete(`/reservation-system/boards/${id}/share-token`);
  },

  async getHolidays(boardId: number, year: number, month: number): Promise<RsHolidayEntry[]> {
    const response = await api.get<{ success: boolean; data: RsHolidayEntry[] }>(
      `/reservation-system/boards/${boardId}/holidays`,
      { params: { year, month } }
    );
    return response.data.data;
  },
};

export const reservationBookingService = {
  async listBookings(boardId: number, year: number, month: number): Promise<ReservationBooking[]> {
    const response = await api.get<{ success: boolean; data: ReservationBooking[] }>(
      `/reservation-system/boards/${boardId}/bookings`,
      { params: { year, month } }
    );
    return response.data.data;
  },

  async createBookings(
    boardId: number,
    input: CreateBookingInput
  ): Promise<ReservationBooking[]> {
    const response = await api.post<{ success: boolean; data: ReservationBooking[] }>(
      `/reservation-system/boards/${boardId}/bookings`,
      input
    );
    return response.data.data;
  },

  async deleteBooking(bookingId: number, password?: string): Promise<void> {
    await api.delete(`/reservation-system/bookings/${bookingId}`, {
      data: password ? { password } : {},
    });
  },
};
